import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { whatsappLink } from "../content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 text-slate-700">
    <div className="bg-white border border-slate-200 soft-shadow rounded-3xl p-10 max-w-md w-full text-center">{children}</div>
  </div>
);

export const PaySuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
      } catch { /* keep polling */ }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <Shell>
      {state === "checking" && (
        <>
          <Loader2 className="w-10 h-10 text-[#0055FF] animate-spin mx-auto" />
          <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900 tracking-tight">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-slate-500">This takes a few seconds.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" data-testid="pay-success-icon" />
          <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900 tracking-tight">Payment received — thank you.</h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Your plan is active. We'll be in touch shortly to get everything set up for your website.
            Want to speed things up?{" "}
            <a href={whatsappLink("Hi WebStudios, I've just paid for a plan online.")} target="_blank" rel="noopener noreferrer" data-testid="pay-success-whatsapp" className="text-[#25D366] hover:underline">Message us on WhatsApp</a>.
          </p>
          <a href="/" data-testid="pay-success-home" className="mt-7 inline-flex items-center justify-center bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200">Back to WebStudios</a>
        </>
      )}
      {(state === "pending" || state === "failed") && (
        <>
          <Loader2 className="w-10 h-10 text-cyan-600 animate-spin mx-auto" />
          <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900 tracking-tight">Almost there…</h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Your payment is still being confirmed. If you were charged, you're all set —{" "}
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:underline">message us on WhatsApp</a> and we'll confirm manually.
          </p>
        </>
      )}
    </Shell>
  );
};

export const PayCancel = () => (
  <Shell>
    <XCircle className="w-12 h-12 text-slate-300 mx-auto" data-testid="pay-cancel-icon" />
    <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900 tracking-tight">Payment cancelled.</h1>
    <p className="mt-3 text-sm text-slate-600 leading-relaxed">No charge was made. You can pick a plan again whenever you're ready — or talk to us first if you have questions.</p>
    <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
      <a href="/pay" data-testid="pay-cancel-retry" className="inline-flex items-center justify-center bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200">Choose a plan</a>
      <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="pay-cancel-whatsapp" className="inline-flex items-center justify-center border border-slate-300 hover:border-slate-900 text-slate-700 hover:text-slate-900 text-sm font-medium px-6 py-3 rounded-full transition-[border-color,color] duration-200">Ask us first</a>
    </div>
  </Shell>
);
